import React, { useState, useEffect } from "react";
import styled from "styled-components";

interface ImageDropzoneDisabledProps {
  base64: string;
  width?: number;
  height?: number;
  uploadMessage?: string;
}

interface DropContainerProps {
  hasImage: boolean;
  customHeight?: number;
}

const DropContainer = styled.div<DropContainerProps>`
  height: ${({ hasImage, customHeight }) =>
    hasImage ? `${customHeight ?? 145}px` : "auto"};
  border: 2px dashed #cccccc;
  border-radius: 4px;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: not-allowed;
  background-color: white;
  
  &:hover {
    border-color: #cccccc;
  }
`;

const MediaContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UploadedImage = styled.img<{ width?: number; height?: number }>`
  max-width: calc(100% - 40px);
  max-height: ${({ height }) =>
    height ? `calc(${height}px - 60px)` : "125px"};
  object-fit: cover;
`;

const UploadedVideo = styled.video<{ width?: number; height?: number }>`
  max-width: calc(100% - 40px);
  max-height: ${({ height }) =>
    height ? `calc(${height}px - 60px)` : "125px"};
  object-fit: cover;
`;

const NoImageMessage = styled.p`
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #999999;
`;

const ImageDropzoneDisabled: React.FC<ImageDropzoneDisabledProps> = ({
  base64,
  width,
  height,
  uploadMessage,
}) => {
  const [selectedMedia, setSelectedMedia] = useState<string | null>(base64);
  const [fileType, setFileType] = useState<"image" | "video">("image");

  useEffect(() => {
    setSelectedMedia(base64);
  }, [base64]);

  useEffect(() => {
    if (selectedMedia) {
      if (selectedMedia.startsWith("data:")) {
        const regex = /^data:(image|video)/;
        const match = selectedMedia.match(regex);
        if (match) {
          setFileType(match[1] as "image" | "video");
        } else {
          setFileType("image");
        }
      } else {
        const lowerUrl = selectedMedia.toLowerCase();
        const extensionRegex = /\.(\w+)(?:\?.*)?$/;
        const match = lowerUrl.match(extensionRegex);
        if (match) {
          const ext = match[1];
          if (["png", "jpg", "jpeg", "gif"].includes(ext)) {
            setFileType("image");
          } else if (["mp4", "avi", "mov", "webm"].includes(ext)) {
            setFileType("video");
          } else {
            setFileType("image");
          }
        } else {
          setFileType("image");
        }
      }
    }
  }, [selectedMedia]);

  return (
    <DropContainer hasImage={!!selectedMedia} customHeight={height}>
      {selectedMedia ? (
        <MediaContainer>
          {fileType === "image" ? (
            <UploadedImage src={selectedMedia} width={width} height={height} />
          ) : (
            <UploadedVideo src={selectedMedia} width={width} height={height} controls />
          )}
        </MediaContainer>
      ) : (
        <NoImageMessage>
          {uploadMessage || "Publicação sem mídia"}
        </NoImageMessage>
      )}
    </DropContainer>
  );
};

export { ImageDropzoneDisabled };
